import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from "@/hooks/useToast";

// Types for Job Card Materials
export interface JobCardMaterial {
  id: string;
  work_order_id: string;
  job_card_id?: string;
  bom_component_id?: string;
  material_id?: string;
  material_name: string;
  material_code: string;
  required_quantity: number;
  available_quantity: number;
  issued_quantity: number;
  shortage_quantity: number;
  unit: string;
  status: 'Not Issued' | 'Partially Issued' | 'Fully Issued' | 'Shortage';
  location?: string;
  created_at: string;
  updated_at: string;
}

export interface MaterialIssueRequest {
  jobCardId: string;
  materialId: string;
  quantity: number;
  location: string;
  issuedBy: string;
  remarks?: string;
}

export interface MaterialReturnRequest {
  jobCardId: string;
  materialId: string;
  quantity: number;
  location: string;
  returnedBy: string;
  reason: string;
  remarks?: string;
}

type UUID = string;

/**
 * Hook for managing Job Card Materials
 * Handles material requirements, issuing, returns, shortage management, and inventory integration
 */
export const useJobCardMaterials = () => {
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState<JobCardMaterial[]>([]);
  const { toast } = useToast();

  /**
   * Fetch materials required for a job card
   */
  const fetchJobCardMaterials = useCallback(async (jobCardId: UUID): Promise<JobCardMaterial[]> => {
    try {
      setLoading(true);
      
      // First get the job card to find work_order_id
      const { data: jobCardData, error: jobCardError } = await supabase
        .from('job_cards')
        .select('work_order_id')
        .eq('id', jobCardId)
        .single();
      
      if (jobCardError) throw jobCardError;
      
      // Fetch materials from work_order_materials with BOM component details
      const { data, error } = await supabase
        .from('work_order_materials')
        .select(`
          *,
          bom_component:bom_component_id(
            component_name,
            drawing_number,
            material,
            unit,
            raw_material_id
          )
        `)
        .eq('work_order_id', jobCardData.work_order_id)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      // Map to JobCardMaterial format and calculate shortages
      const jobCardMaterials: JobCardMaterial[] = (data || []).map((item: any) => {
        const availableQty = item.available_quantity || 0;
        const requiredQty = item.required_quantity || 0;
        const issuedQty = item.issued_quantity || 0;
        const shortageQty = Math.max(0, requiredQty - availableQty);
        
        // Determine status based on quantities
        let status: JobCardMaterial['status'] = 'Not Issued';
        if (shortageQty > 0) {
          status = 'Shortage';
        } else if (issuedQty >= requiredQty) {
          status = 'Fully Issued';
        } else if (issuedQty > 0) {
          status = 'Partially Issued';
        }
        
        return {
          id: item.id,
          work_order_id: item.work_order_id,
          job_card_id: jobCardId,
          bom_component_id: item.bom_component_id,
          material_id: item.material_id,
          material_name: item.bom_component?.component_name || 'Unknown Material',
          material_code: item.bom_component?.drawing_number || 'N/A',
          required_quantity: requiredQty,
          available_quantity: availableQty,
          issued_quantity: issuedQty,
          shortage_quantity: shortageQty,
          unit: item.bom_component?.unit || 'pcs',
          status,
          location: undefined, // Will be fetched from inventory
          created_at: item.created_at,
          updated_at: item.updated_at,
        };
      });
      
      setMaterials(jobCardMaterials);
      return jobCardMaterials;
    } catch (error: any) {
      toast({
        title: 'Error fetching materials',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Issue material for a job card
   */
  const issueMaterial = useCallback(async (request: MaterialIssueRequest): Promise<boolean> => {
    try {
      setLoading(true);
      
      // First check if enough quantity is available
      const material = materials.find(m => m.id === request.materialId);
      if (!material) {
        throw new Error('Material not found');
      }
      
      const newIssuedQty = material.issued_quantity + request.quantity;
      if (newIssuedQty > material.required_quantity) {
        throw new Error(`Cannot issue more than required quantity (${material.required_quantity} ${material.unit})`);
      }
      
      if (request.quantity > material.available_quantity) {
        throw new Error(`Insufficient inventory. Available: ${material.available_quantity} ${material.unit}`);
      }
      
      // Update work_order_materials table
      const { error: updateError } = await supabase
        .from('work_order_materials')
        .update({
          issued_quantity: newIssuedQty,
          available_quantity: material.available_quantity - request.quantity,
          status: newIssuedQty >= material.required_quantity ? 'Fully Issued' : 'Partially Issued',
          updated_at: new Date().toISOString()
        })
        .eq('id', request.materialId);
      
      if (updateError) throw updateError;
      
      // Create inventory transaction record for automatic stock updates via database triggers
      // This uses the inventory_tx table which has triggers to update warehouse_stock and inventory_items.stock_level
      const currentUser = (await supabase.auth.getUser()).data.user;
      
      if (material.material_id) {
        const { error: txError } = await supabase
          .from('inventory_tx')
          .insert({
            txn_type: 'ISSUE-TO-PRODUCTION',
            inventory_item_id: material.material_id, // Links to inventory_items for trigger
            item_id: material.bom_component_id, // Legacy link to bom_components
            qty: request.quantity,
            uom: material.unit || 'pcs',
            wh_from: request.location, // Warehouse ID from which material is issued
            ref_table: 'work_order_materials',
            ref_id: request.materialId,
            note: request.remarks || `Material issued for Job Card ${request.jobCardId}`,
            created_by: currentUser?.id
          });

        if (txError) {
          console.error('Error creating inventory transaction:', txError);
        }
      }
      
      // Create job card activity
      await supabase
        .from('job_card_activities')
        .insert({
          job_card_id: request.jobCardId,
          activity_type: 'material_issued',
          description: `Issued ${request.quantity} units of ${material.material_name}`,
          user_id: (await supabase.auth.getUser()).data.user?.id || 'current-user',
          metadata: {
            table_name: 'work_order_materials',
            record_id: request.materialId,
            action: 'UPDATE',
            field_name: 'issued_quantity',
            old_value: material.issued_quantity,
            new_value: newIssuedQty,
            change_description: `Issued ${request.quantity} units of ${material.material_name}`,
            category: 'material',
            severity: 'info'
          },
          timestamp: new Date().toISOString()
        });
      
      // Refresh materials list
      await fetchJobCardMaterials(request.jobCardId);
      
      toast({
        title: 'Material Issued',
        description: `${request.quantity} ${material.unit} of ${material.material_name} issued successfully`,
        variant: 'default',
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Error issuing material',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [materials, fetchJobCardMaterials, toast]);

  /**
   * Return material from a job card
   */
  const returnMaterial = useCallback(async (request: MaterialReturnRequest): Promise<boolean> => {
    try {
      setLoading(true);
      
      const material = materials.find(m => m.id === request.materialId);
      if (!material) {
        throw new Error('Material not found');
      }
      
      if (request.quantity > material.issued_quantity) {
        throw new Error(`Cannot return more than issued quantity (${material.issued_quantity} ${material.unit})`);
      }
      
      const newIssuedQty = material.issued_quantity - request.quantity;
      const newAvailableQty = material.available_quantity + request.quantity;
      
      // Update work_order_materials table
      const { error: updateError } = await supabase
        .from('work_order_materials')
        .update({
          issued_quantity: newIssuedQty,
          available_quantity: newAvailableQty,
          status: newIssuedQty === 0 ? 'Not Issued' : 'Partially Issued',
          updated_at: new Date().toISOString()
        })
        .eq('id', request.materialId);
      
      if (updateError) throw updateError;
      
      // Create inventory transaction record for automatic stock updates via database triggers
      const currentUser = (await supabase.auth.getUser()).data.user;
      
      if (material.material_id) {
        const { error: txError } = await supabase
          .from('inventory_tx')
          .insert({
            txn_type: 'WIP_RETURN',
            inventory_item_id: material.material_id, // Links to inventory_items for trigger
            item_id: material.bom_component_id, // Legacy link to bom_components
            qty: request.quantity,
            uom: material.unit || 'pcs',
            wh_to: request.location, // Warehouse ID to which material is returned
            ref_table: 'work_order_materials',
            ref_id: request.materialId,
            note: `${request.reason}${request.remarks ? ` - ${request.remarks}` : ''}`,
            created_by: currentUser?.id
          });

        if (txError) {
          console.error('Error creating inventory transaction:', txError);
        }
      }
      
      // Refresh materials list
      await fetchJobCardMaterials(request.jobCardId);
      
      toast({
        title: 'Material Returned',
        description: `${request.quantity} ${material.unit} of ${material.material_name} returned successfully`,
        variant: 'default',
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Error returning material',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [materials, fetchJobCardMaterials, toast]);

  /**
   * Handle material shortage by creating purchase requisition
   */
  const handleMaterialShortage = useCallback(async (materialId: UUID, shortageQty: number): Promise<boolean> => {
    try {
      setLoading(true);
      
      const material = materials.find(m => m.id === materialId);
      if (!material) {
        throw new Error('Material not found');
      }
      
      // Create purchase requisition for shortage
      const { error } = await supabase
        .from('purchase_requisitions')
        .insert({
          material_id: material.material_id,
          material_name: material.material_name,
          material_code: material.material_code,
          requested_quantity: shortageQty,
          unit: material.unit,
          urgency: 'High',
          reason: 'Job Card Material Shortage',
          requested_by: (await supabase.auth.getUser()).data.user?.id || 'current-user',
          work_order_id: material.work_order_id,
          job_card_id: material.job_card_id,
          status: 'Pending',
          requested_date: new Date().toISOString()
        });
      
      if (error) throw error;
      
      toast({
        title: 'Purchase Requisition Created',
        description: `PR created for ${shortageQty} ${material.unit} of ${material.material_name}`,
        variant: 'default',
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Error creating purchase requisition',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [materials, toast]);

  /**
   * Update material availability from inventory
   */
  const updateMaterialAvailability = useCallback(async (jobCardId: UUID): Promise<void> => {
    try {
      // Get current materials
      const currentMaterials = await fetchJobCardMaterials(jobCardId);
      
      // Update availability from inventory system
      for (const material of currentMaterials) {
        if (material.material_id) {
          const { data: inventoryData } = await supabase
            .from('inventory_items')
            .select('available_quantity, location')
            .eq('id', material.material_id)
            .single();
          
          if (inventoryData) {
            await supabase
              .from('work_order_materials')
              .update({
                available_quantity: inventoryData.available_quantity,
                updated_at: new Date().toISOString()
              })
              .eq('id', material.id);
          }
        }
      }
      
      // Refresh materials list
      await fetchJobCardMaterials(jobCardId);
    } catch (error: any) {
      console.error('Error updating material availability:', error);
    }
  }, [fetchJobCardMaterials]);

  /**
   * Get material transaction history for a job card
   */
  const getMaterialTransactions = useCallback(async (jobCardId: UUID): Promise<any[]> => {
    try {
      const { data, error } = await supabase
        .from('material_transactions')
        .select(`
          *,
          material:material_id(material_name, material_code),
          performer:performed_by(full_name)
        `)
        .eq('job_card_id', jobCardId)
        .order('transaction_date', { ascending: false });
      
      if (error) throw error;
      
      return data || [];
    } catch (error: any) {
      toast({
        title: 'Error fetching material transactions',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    }
  }, [toast]);

  return {
    loading,
    materials,
    fetchJobCardMaterials,
    issueMaterial,
    returnMaterial,
    handleMaterialShortage,
    updateMaterialAvailability,
    getMaterialTransactions
  };
};
